import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import {
  addSnackbar,
  Button,
  dismissSnackbar,
  Spacer,
} from 'theSource/components';
import {
  useConfigurationQuery,
  useContentSettingsQuery,
} from 'theSource/queries';
import { hasDeniedPermissions, useApiMutation } from 'theSource/utils';
import {
  RegistrationDatatDto,
  RegistrationErrorDto,
  RegistrationOptionsResponseDto,
  RegistrationResponseDto,
} from './types';
import {
  getErrorSnackbar,
  mapToCredentialCreationOptions,
  mapToRegistrationData,
  REGISTRATION_NEW_QUERY_KEY,
  REGISTRATION_QUERY_KEY,
} from './utils';
import {
  CancelButtonStyled,
  DialogActionsStyled,
  DialogStyled,
  DialogContentStyled,
  DialogTitleStyled,
  DIALOG_Z_INDEX,
} from './NativeAuthDialogStyled';
import {
  selectNativeAuth,
  setIsEnabled,
  setIsRegistered,
} from 'theSource/services';
import { selectIsAuthenticated } from 'theSource/features';

export const NativeAuthDialog = () => {
  const { isSupported, isRegistered, isEnabled } =
    useSelector(selectNativeAuth);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { configuration } = useConfigurationQuery();
  const { t } = useTranslation('messages.dialog');
  const dispatch = useDispatch();

  const { contentSettings } = useContentSettingsQuery({
    enabled: Boolean(isAuthenticated && configuration?.apiHost),
  });

  const [isLoading, setIsLoading] = useState(false);

  const baseUrl = configuration?.apiHost;

  const { mutateAsync: registerNew } = useApiMutation<
    RegistrationOptionsResponseDto,
    RegistrationErrorDto,
    Object
  >({
    queryKey: REGISTRATION_NEW_QUERY_KEY,
    endpoint: `${baseUrl}/web_authn/registration/new`,
    method: 'post',
    showSuccessSnackbar: false,
    showErrorSnackbar: false,
  });

  const { mutateAsync: register } = useApiMutation<
    RegistrationResponseDto,
    RegistrationErrorDto,
    RegistrationDatatDto
  >({
    queryKey: REGISTRATION_QUERY_KEY,
    endpoint: `${baseUrl}/web_authn/registration`,
    method: 'post',
    showSuccessSnackbar: false,
    showErrorSnackbar: false,
  });

  const handleRegistration = async () => {
    try {
      setIsLoading(true);
      dispatch(setIsEnabled(true));

      const registrationOptions = await registerNew({});

      const credentialCreationOptions =
        mapToCredentialCreationOptions(registrationOptions);

      const credential = await navigator.credentials.create(
        credentialCreationOptions
      );

      if (!credential) {
        throw new Error('Registration was not completed');
      }

      const registrationData = mapToRegistrationData(
        credential as PublicKeyCredential
      );

      await register(registrationData);

      dispatch(setIsRegistered(true));
      setIsLoading(false);

      dispatch(
        addSnackbar({
          message: t('nativeAuthDialogSuccessMessage'),
          severity: 'success',
        })
      );
    } catch (error) {
      setIsLoading(false);
      if (hasDeniedPermissions(error)) {
        return;
      }
      dispatch(addSnackbar(getErrorSnackbar(DIALOG_Z_INDEX, t)));
    }
  };

  const handleCancelClick = () => {
    dispatch(setIsEnabled(false));
    dispatch(dismissSnackbar());
  };

  const isOpened =
    isEnabled &&
    isSupported &&
    !isRegistered &&
    contentSettings?.user_name !== (null || undefined);

  return (
    <DialogStyled data-testid="native-auth-dialog" open={isOpened} fullScreen>
      <DialogActionsStyled>
        <CancelButtonStyled
          data-testid="cancel-button"
          onClick={handleCancelClick}
          label={t('common:cancel')}
          variant="tertiary"
          fullWidth={false}
          disabled={isLoading}
        />
      </DialogActionsStyled>
      <DialogContentStyled>
        <Spacer height={8} />
        <DialogTitleStyled variant="body2">
          {t('nativeAuthDialogTitle')}
        </DialogTitleStyled>
        <Spacer height={8} />
        <Button
          data-testid="continue-button"
          onClick={handleRegistration}
          label={t('common:continue')}
          variant="primary"
          fullWidth={false}
          isLoading={isLoading}
        />
      </DialogContentStyled>
    </DialogStyled>
  );
};
